import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Linkedin, Instagram, Loader2, Send } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/newsletter/subscribe", { email });
      toast.success("Inscription confirmée", { description: "Un email de bienvenue vient de vous être envoyé." });
      setEmail("");
    } catch (err) {
      toast.error("Erreur", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  return (
    <footer className="mt-32 bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="container-x py-16 grid gap-12 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/logo-beehive.png" alt="Beehive Academy" className="h-11 w-11 object-contain" />
            <span className="font-display font-bold text-lg text-white">Beehive Enfance et Éducation Academy</span>
          </div>
          <p className="text-sm leading-relaxed text-slate-400 max-w-xs">
            L'Académie Beehive est l'espace de formation de Beehive Enfance et Éducation. Neurosciences appliquées à la petite enfance, pour transformer votre posture professionnelle.
          </p>
          <a
            href="https://www.beehive-enfance-et-education.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-beehive-link"
            className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-yellow-400 hover:text-yellow-300"
          >
            En savoir plus sur Beehive →
          </a>
          <form onSubmit={subscribe} className="mt-6" data-testid="newsletter-form">
            <div className="eyebrow text-yellow-400 mb-2">Lettre d'information trimestrielle</div>
            <div className="flex gap-2">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                placeholder="votre@email.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 min-w-0 rounded-full bg-slate-800 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-400"
              />
              <button data-testid="newsletter-submit" disabled={loading} aria-label="S'inscrire à la newsletter" className="grid place-items-center h-10 w-10 shrink-0 rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-colors disabled:opacity-70">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Désinscription à tout moment.</p>
          </form>
        </div>
        <div>
          <div className="eyebrow text-yellow-400 mb-4">Offre</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/formations" className="hover:text-white">Catalogue des formations</Link></li>
            <li><Link to="/offre" className="hover:text-white">Notre offre</Link></li>
            <li><Link to="/offre#devis" className="hover:text-white">Devis personnalisé</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-yellow-400 mb-4">Ressources</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/guides" className="hover:text-white">Guides gratuits</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/a-propos" className="hover:text-white">À propos</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-yellow-400 mb-4">Contact</div>
          <a href="mailto:info@beehivemasterclass.info" className="flex items-center gap-2 text-sm hover:text-white">
            <Mail size={16} strokeWidth={1.5} /> info@beehivemasterclass.info
          </a>
          <a href="https://www.beehive-enfance-et-education.com/" target="_blank" rel="noopener noreferrer" data-testid="footer-asbl-link" className="block mt-3 text-sm hover:text-white underline underline-offset-2">
            Site de l'ASBL Beehive Enfance et Éducation (siège)
          </a>
          <div className="flex gap-3 mt-4">
            <a href="https://www.linkedin.com/company/beehive-ee/" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn" className="p-2 rounded-full bg-slate-800 hover:bg-slate-700"><Linkedin size={16} /></a>
            <a href="https://www.instagram.com/beehiveenfanceeteducation" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="p-2 rounded-full bg-slate-800 hover:bg-slate-700"><Instagram size={16} /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500 gap-2">
          <span>© {new Date().getFullYear()} Beehive Academy. Tous droits réservés.</span>
          <span>Neurosciences · Petite enfance · Formation continue</span>
        </div>
      </div>
    </footer>
  );
}
